import { useContext, useEffect, useState } from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import Header from "./components/common/Header"
import Footer from "./components/common/Footer"
import BaseLayout from "./components/layout/BaseLayout"
import ErrorPage from "./pages/404/ErrorPage"
import PageLoader from "./pages/PageRendererAndLoader/PageLoader"
import { useQuery } from "@tanstack/react-query"
import { rearrangeNavigationDataFromSulu } from "./services/functions"
import { NavigationItem } from "./types"
import { getDynamicData, getNavigation, getSnippetData } from "./api/apiCall"
import Loading from "./components/common/Loading"
import "./style/colors.css"
import { useAccessibility } from "./context/AccessibilityContext"
import { ThemeContext } from "./context/ThemeContext"
import NavigationContentQuery from "./api/generalQuerys"
import BlogPage from "./pages/PageRendererAndLoader/BlogPage"

function App() {
    const { contrast, easySpeech } = useAccessibility()

    // Content Request
    const location = useLocation()
    const {
        data: pageContent,
        isLoading: pageContentIsLoading,
        error: pageContentError,
    } = useQuery({
        queryKey: ["pageData", location.pathname + (easySpeech ? "/simple" : "/de")],
        queryFn: () => getDynamicData({ lang: easySpeech ? "/simple" : "/de", slug: location.pathname }),
        enabled: !!location.pathname && !location.pathname.includes("blog"),
    })

    // Request to get header navigation links
    const {
        data: navigationData,
        isLoading: navbarIsLoading,
        error: navbarError,
    } = useQuery({
        queryKey: ["website-navbar-request" + (easySpeech ? "/simple" : "/de")],
        queryFn: () => getNavigation({ lang: easySpeech ? "/simple" : "/de", context: "main" }),
    })

    // Request to get footer navigation links
    const {
        data: footerNavigationData,
        isLoading: footerNavigationIsLoading,
        error: footerNavigationError,
    } = useQuery({
        queryKey: ["website-footerNav-request" + (easySpeech ? "/simple" : "/de")],
        queryFn: () => getNavigation({ lang: easySpeech ? "/simple" : "/de", context: "footer_left" }),
    })

    const [navbar, setnavbar] = useState<NavigationItem[]>()
    const [footerNav, setfooterNav] = useState<NavigationItem[]>()
    useEffect(() => {
        if (navigationData) {
            setnavbar(rearrangeNavigationDataFromSulu(navigationData))
        }
        if (footerNavigationData) {
            setfooterNav(rearrangeNavigationDataFromSulu(footerNavigationData))
        }
    }, [navigationData, footerNavigationData])

    // Snippets Laden
    const {
        data: footerContent,
        isLoading: footerContentIsLoading,
        error: footerContentError,
    } = useQuery({
        queryKey: ["snippet-footer" + (easySpeech ? "/simple" : "/de")],
        queryFn: () => getSnippetData({ lang: easySpeech ? "/simple" : "/de", snippet: "footer" }),
    })

    const { getCursorClass } = useAccessibility()

    // PageColorTracker
    const color = useContext(ThemeContext)
    useEffect(() => {
        if (pageContent?.content && !contrast) {
            color?.setcolor({
                text: pageContent?.content.pageSettingColorText || "#fff",
                button: pageContent?.content.pageSettingColorDark || "#fff",
                bg1: pageContent?.content.pageSettingColorPrimary || "#fff",
                bg2: pageContent?.content.pageSettingColorLight || "#fff",
                bg3: pageContent?.content.pageSettingColorSuperLight || "#fff",
                in: pageContent?.content.pageSettingColorOptional || "#fff"
            })
        }
    }, [pageContent?.content, contrast])

    const { navigationContent, navigationContentError, navigationContentIsLoading } = NavigationContentQuery()

    return (
        <main className={`relative overflow-hidden pt-24 ${getCursorClass()}`}>
            {(navbarIsLoading || footerContentIsLoading || footerNavigationIsLoading || navigationContentIsLoading || pageContentIsLoading) && (
                <Loading />
            )}
            {(navbarError || footerContentError || footerNavigationError || navigationContentError || pageContentError) && <ErrorPage />}
            {!navbarIsLoading &&
                !footerContentIsLoading &&
                !footerNavigationIsLoading &&
                !navigationContentIsLoading &&
                !navbarError &&
                !footerContentError &&
                !footerNavigationError &&
                !navigationContentError &&
                !pageContentError &&
                !pageContentIsLoading &&
                navbar &&
                navigationContent &&
                footerContent &&
                footerNavigationData && (
                    <>
                        <Header pages={navbar as NavigationItem[]} content={navigationContent?.content} />
                        <Routes>
                            <Route path="/" element={<Navigate to={"/homepage"} />} />
                            <Route
                                path="/blog"
                                element={<BaseLayout logo={navigationContent.content.navigationLogo.url} children={<BlogPage />} />}
                            />
                            <Route
                                path="/404"
                                element={<BaseLayout logo={navigationContent.content.navigationLogo.url} children={<ErrorPage />} />}
                            />
                            {pageContent && (
                                <Route
                                    path="/*"
                                    element={
                                        <BaseLayout
                                            logo={navigationContent.content.navigationLogo.url}
                                            seo={pageContent.extension.seo}
                                            children={<PageLoader data={pageContent} />}
                                        />
                                    }
                                />
                            )}

                            <Route path="*" element={<Navigate to="/404" />} />
                        </Routes>
                        <Footer pages={footerNav as NavigationItem[]} footerContent={footerContent?.content} />
                    </>
                )}
        </main>
    )
}

export default App
